import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { channelLabelKey, statusVisual, toBookingChannel, toBookingStatus } from "./status";
import { statusBadgeClass, statusDotClass } from "./statusStyles";

/**
 * The status badge — the six-state colour system on screen. Colour comes from `@repo/theme`
 * `booking.*` tokens, but meaning is carried by the LABEL TEXT and a SHAPE glyph, never hue alone
 * (RGAA/WCAG, ADR-0023): a colour-blind or high-contrast user reads the status regardless. An
 * unmapped status value renders neutrally rather than guessing.
 */
export function StatusBadge({ status }: { status: string | null | undefined }) {
  const { t } = useTranslation();
  const known = toBookingStatus(status);
  if (!known) {
    return (
      <span
        className={`${badgeBase} ${css({ bg: "bg.muted", color: "fg.muted", borderColor: "border.default" })}`}
        data-testid="status-badge"
        data-status="UNKNOWN"
      >
        {status ?? t("bookings.status.unknown")}
      </span>
    );
  }
  const v = statusVisual(known);
  return (
    <span
      className={`${badgeBase} ${statusBadgeClass[known]}`}
      data-testid="status-badge"
      data-status={known}
    >
      <span aria-hidden className={`${css({ fontSize: "xs" })} ${statusDotClass[known]}`}>
        {v.shape}
      </span>
      {t(v.labelKey)}
    </span>
  );
}

/**
 * The channel badge (R6). A value outside the canonical nine renders an explicit "needs channel
 * tagging" badge (and its own filter bucket) — never silently defaulted (edge case "channel not yet
 * mapped").
 */
export function ChannelBadge({ channel }: { channel: string | null | undefined }) {
  const { t } = useTranslation();
  const known = toBookingChannel(channel);
  if (!known) {
    return (
      <span
        className={`${badgeBase} ${css({ bg: "bg.muted", color: "fg.muted", borderColor: "border.default" })}`}
        data-testid="channel-badge"
        data-channel="UNMAPPED"
      >
        {t("bookings.channel.unmapped")}
      </span>
    );
  }
  return (
    <span
      className={`${badgeBase} ${css({ bg: "bg.subtle", color: "fg.muted", borderColor: "border.default" })}`}
      data-testid="channel-badge"
      data-channel={known}
    >
      {t(channelLabelKey(known))}
    </span>
  );
}

const badgeBase = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "1",
  px: "2",
  py: "0.5",
  rounded: "md",
  borderWidth: "1px",
  fontSize: "xs",
  fontWeight: "medium",
  whiteSpace: "nowrap",
});
